/**
 * Data table component with sorting and row click support.
 * 
 * @module components/ui/DataTable
 */

import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TableSkeleton } from './LoadingSkeleton';
import { EmptyState } from './EmptyState';
import type { DataTableProps, DataTableColumn } from '../../types';

/**
 * Renders a sortable column header.
 */
function SortableHeader<T>({
  column,
  sortBy,
  sortOrder,
  onSort,
}: {
  column: DataTableColumn<T>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
}) {
  const isSorted = sortBy === column.key;
  const canSort = column.sortable && onSort;

  return (
    <th
      scope="col"
      className={cn(
        'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50',
        canSort && 'cursor-pointer select-none hover:bg-gray-100 transition-colors',
        column.width && `w-[${column.width}]`
      )}
      style={column.width ? { width: column.width } : undefined}
      onClick={() => canSort && onSort(column.key as string)}
    >
      <div className={cn('flex items-center gap-1', column.align === 'right' && 'justify-end')}>
        <span>{column.header}</span>
        {canSort && (
          <span className="flex flex-col">
            <ChevronUp
              className={cn(
                'h-3 w-3 -mb-1',
                isSorted && sortOrder === 'asc' ? 'text-primary-600' : 'text-gray-300'
              )}
            />
            <ChevronDown
              className={cn(
                'h-3 w-3',
                isSorted && sortOrder === 'desc' ? 'text-primary-600' : 'text-gray-300'
              )}
            />
          </span>
        )}
      </div>
    </th>
  );
}

/**
 * Data table component for displaying tabular data.
 * 
 * @param props - DataTable properties
 * @returns DataTable element
 * 
 * @example
 * <DataTable
 *   columns={[
 *     { key: 'email', header: 'Email', sortable: true },
 *     { key: 'name', header: 'Name' },
 *   ]}
 *   data={users}
 *   keyExtractor={(user) => user.id}
 *   onRowClick={(user) => navigate(`/users/${user.id}`)}
 * />
 */
export function DataTable<T>({
  columns,
  data,
  isLoading,
  emptyMessage = 'No data available',
  onRowClick,
  keyExtractor,
  sortBy,
  sortOrder,
  onSort,
}: DataTableProps<T>) {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={columns.length} />;
  }

  if (data.length === 0) {
    return <EmptyState title={emptyMessage} />;
  }

  return (
    <div className="table-container">
      <table className="table" role="grid">
        <thead>
          <tr>
            {columns.map((column) => (
              <SortableHeader
                key={column.key as string}
                column={column}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item) => (
            <tr
              key={keyExtractor(item)}
              className={cn(
                'transition-colors',
                onRowClick && 'cursor-pointer hover:bg-gray-50'
              )}
              onClick={() => onRowClick?.(item)}
              onKeyDown={(e) => e.key === 'Enter' && onRowClick?.(item)}
              tabIndex={onRowClick ? 0 : undefined}
              role="row"
            >
              {columns.map((column) => (
                <td
                  key={column.key as string}
                  className={cn(
                    'px-6 py-4 text-sm text-gray-900 whitespace-nowrap',
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center'
                  )}
                >
                  {column.render
                    ? column.render(item)
                    : (item[column.key as keyof T] as React.ReactNode) ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
